import {useContext} from 'react';
import {Audit} from "@bdfx/audit-api/types"
import { css, Grid, Icon, Button, Div } from "@bdfx/fukit"
import {formatInt, formatPNO } from "../../common/format"
import Chart from "chart.js"
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {eventGA} from "../../common/tracking"

import history from "../../common/history"
import Results from "./results"
import ResultHeader from "./result-header"
import Categories from "./categories"
import Competitors from "./competitors"
import Footer from "./footer"
import ctrans from "../../common/trans"
import trans from "../trans"
import {AuditContext} from "../providers/AuditProvider"
import {Placeholder} from "../app"
import {getColor, ERRORS, WARNINGS, SUGGESTIONS, getProblemUrl, }  from "../helpers"
import {getAuditId} from "../../common/helpers"
import ErrorPage from "../app-components/error-page"
import PagePositionReporter from './position-reporter';
import PricingFoxCtaButton from '../app-components/pricingfox-cta-button';
Chart.pluginService.register(ChartDataLabels);


export const getIcon = (index) => {
    switch(index) {
        case "group_ok": 
            return "cup-light"
        case "group_problems": 
            return "spam-light"
        case "group_warnings": 
            return "danger-light"
        case "group_recommendations": 
        default:
            return "info-light"
    }
}

function Result() {
    const [data]: [Audit] = useContext(AuditContext)

    if(getAuditId() === "example") {
        document.title = ctrans("example_results") + " | Pricing Audit"
    } else {
        if(data?.shop?.name)
        document.title = ctrans("audit_result") + ": " + data.shop.name + " | Pricing Audit"
    }

    if(!data) {
        return <Placeholder /> 
    }

    const { status, result } = data
    const { group_problems } = result
    
    if(status === "working" && !result) {
        eventGA("AUDIT", "Audit generating", "AUDIT_IN_PROGRESS")

        return <ErrorPage code={400} />
    }

    if(getAuditId() !== "example") {
        eventGA("AUDIT", "Audit detail", "AUDIT_DETAIL")
    }

    const errors = ERRORS.reduce((acc,index) => ({...acc, [index]: result?.[index]}) ,{})
    const warnings = WARNINGS.reduce((acc,index) => ({...acc, [index]: result?.[index]}) ,{})
    const suggestions = SUGGESTIONS.reduce((acc,index) => ({...acc, [index]: result?.[index]}) ,{})
    const errorsCount = result?.group_problems
    const warningsCount = result?.group_warnings
    const suggestionsCount = result?.group_recommendations
    const okCount = result?.group_ok

    const pairedToAllRatio = result?.paired / result?.total_products
    
    const goToDetail = (index) => (e) => { 
        e.preventDefault()
        history.push(getProblemUrl(data.id, index))
        window.scrollTo(0,0)
        e.stopPropagation()
        return false
    }
    const total = result?.total_products


    const competitors = [{
            "name": trans("my_products"),
            "better_price": 0,
            "same_price": total,
            "worse_price": 0,
            "count": total,
            "count_sum": total
    }, ...(result?.competitors ?? [])]


    return (
    <Style>
        <ResultHeader data={data}>
            <p>
                {trans("audit_result_info", {
                    total: formatInt(result?.total_products),
                    paired: formatInt(result?.paired),
                    paired_ratio: formatPNO(pairedToAllRatio * 100),
                    most_position: formatInt(result?.price_positions_stats?.mode),
                    avg_offers: formatInt(result?.shops_count_stats?.avg),
                })} 
                <small style={{ display: "block", textAlign: "right"}}><em>{trans("audit_result_paired_hint")}</em></small>
            </p>
            <Grid className="flags" cols="1fr 1fr 1fr 1fr" gap="50px">
                <SummaryBox index={"group_ok"}>
                    <span className="products-total">
                        {formatPNO(Math.round(okCount * 100 / total))}
                    </span>
                    <br/>
                    {formatInt(okCount)} {ctrans("of")} {formatInt(total)}
                    <br/>
                    {ctrans("n_products", {n: -1})}
                    <br/>
                    <Icon color="white" size="35" type={getIcon("group_ok")} />
                    <br/>
                    <strong>{trans("group_ok").toLowerCase()}</strong>
                    <br/>
                    <Button 
                            as="a" href={getProblemUrl(data.id, 'group_ok')} 
                            onClick={goToDetail('group_ok')}
                            secondary={true} color="white" size="small" 
                            icon="chevron-right-light" style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                </SummaryBox>

                <SummaryBox index={"group_problems"}>
                    <span className="products-total">
                    {formatPNO(Math.round(errorsCount * 100 / total))}
                    </span>
                    <br/>
                    {formatInt(errorsCount)} {ctrans("of")} {formatInt(total)}
                    <br/>
                    {ctrans("n_products", {n: -1})}
                    <br/>
                    <Icon color="white" size="35" type={getIcon("group_problems")} />
                    <br/>
                    <strong>{trans("group_problems").toLowerCase()}</strong>
                    <br/>
                    <Button as="a" href={getProblemUrl(data.id, 'group_problems')} onClick={goToDetail('group_problems')} secondary={true} color="white" size="small" icon="chevron-right-light" style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                </SummaryBox>

                <SummaryBox index={"group_warnings"}>
                    <span className="products-total">
                    {formatPNO(Math.round(warningsCount * 100 / total))}
                    </span>
                    <br/>
                    {formatInt(warningsCount)} {ctrans("of")} {formatInt(total)}
                    <br/>
                    {ctrans("n_products", {n: -1})}
                    <br/>
                    <Icon color="white" size="35" type={getIcon("group_warnings")} />
                    <br/>
                    <strong>{trans("group_warnings").toLowerCase()}</strong>
                    <br/>
                    <Button as="a" href={getProblemUrl(data.id, 'group_warnings')} 
                            onClick={goToDetail('group_warnings')}
                            secondary={true} color="white" size="small" icon="chevron-right-light" 
                            style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                </SummaryBox>

                <SummaryBox index={"group_recommendations"}>
                <span className="products-total">
                    {formatPNO(Math.round(suggestionsCount * 100 / total))}
                    </span>
                    <br/>
                    {formatInt(suggestionsCount)} {ctrans("of")} {formatInt(total)}
                    <br/>
                    {ctrans("n_products", {n: -1})}
                    <br/>
                    <Icon color="white" size="35" type={getIcon("group_recommendations")} />
                    <br/>
                    <strong>{trans("group_recommendations").toLowerCase()}</strong>
                    <br/>
                    <Button as="a" href={getProblemUrl(data.id, 'group_recommendations')} 
                            onClick={goToDetail('group_recommendations')}
                            secondary={true} color="white" size="small" icon="chevron-right-light" 
                            style={{ marginTop: "10px",}}>{ctrans("detail")}</Button>
                </SummaryBox>
            </Grid>
            <Div marginBlock="4em -0.5em">
                <PricingFoxCtaButton text={<span>{formatInt(group_problems)} {trans("n_problems")}? {trans("pf_cta_banner_line1")} {trans("pf_cta_banner_line2")}</span>}/>
            </Div>
        </ResultHeader>
        <PagePositionReporter trackingKey='result_page_header' auditId={data.id} />

        <Categories top_categories={result?.top_categories} />
        <div className="pbreak" />
        <Competitors competitors={competitors} />
        <div className="pbreak" />
        <PagePositionReporter trackingKey='result_page_competitors' auditId={data.id} />
        <Results auditId={data.id} errors={errors} warnings={warnings} suggestions={suggestions} total={result?.total_products} />
        <PagePositionReporter trackingKey='result_page_footer' auditId={data.id} />
        <Footer />
    </Style>
  );
}

export const Style = css("div")({
    ".flags": {
        maxWidth: "1000px", 
        margin: "auto",
    },

    "@media only screen and (max-width: 1000px)": {
        ".flags": {
            display: "block",
            " > div": {
                display: "inline-block",
                margin: "5px",
            },
        },
    },
})

export const SummaryBox = css("div")({
    padding: "25px",
    fontSize: "1.4em",
    lineHeight: "1.4em",
    color: "white",
    paddingBottom: "60px",
    clipPath: "polygon(0% 0, 100% 0%, 100% 80%, 50% 100%, 0 80%)",
    "> .fuk-icon": {
        marginTop: "10px",
        marginBottom: "10px",
    },
    ".fuk-button": {
        fontSize: "1em",
        display: "inline-block",
    },
    ".products-total": {
        fontSize: "2em",
        fontWeight: "bold",
        display: "block",
    },

}, props => ({
    background: getColor(props.index),
}))

export default Result;